import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /**
   * In-flight prefetches keyed by request URL, so fetchUpdatedSection can adopt one instead of
   * starting over. Holds response *text* rather than relying on the HTTP cache, which section
   * responses aren't guaranteed to allow. Capped — a shopper trying several swatches shouldn't
   * accumulate responses.
   * @type {Map<string, Promise<string>>}
   */
  #prefetchCache = new Map();

  static #PREFETCH_LIMIT = 4;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /**
   * Tracks how many times the current change has re-fetched to cascade the
   * selection to the first available downstream option. Reset on every
   * user-initiated change and bounded by the number of options.
   * @type {number}
   */
  #autoSelectDepth = 0;

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));

    // Warm the request while the finger/mouse is still down. pointerdown lands well before the
    // click that follows it, so the response is usually already in flight — often already home —
    // by the time variantChanged asks for it.
    this.addEventListener('pointerdown', this.#prefetchOnPointerDown);
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // This is a fresh, user-initiated change, so reset the cascade counter that
    // bounds the auto-selection re-fetch loop (see fetchUpdatedSection).
    this.#autoSelectDepth = 0;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    // Reset every option that comes after the one the user just changed, so the
    // re-render resolves each to its first available value (e.g. changing Size
    // picks the first available Planter, then that planter's first Color). The
    // cleared values are dropped from the request; the server (and the cascade
    // in fetchUpdatedSection) fills them with the first available option.
    if (isOnProductPage && morphElementSelector === undefined) {
      this.#clearDownstreamOptions(event.target);
    }

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Starts the request a pointerdown is about to cause, so the click doesn't wait on the network
   * from scratch. Silent: a prefetch that turns out to be wrong (or never used) is discarded.
   * @param {PointerEvent} event
   */
  #prefetchOnPointerDown = (event) => {
    if (!(event.target instanceof Element)) return;

    const input =
      event.target.closest('input[type="radio"]') ??
      event.target.closest('label')?.querySelector('input[type="radio"]');

    if (!(input instanceof HTMLInputElement) || input.checked) return;

    const optionValues = this.#predictedOptionValues(input);
    if (!optionValues?.length) return;

    const requestUrl = this.buildRequestUrl(input, null, [], optionValues);
    if (this.#prefetchCache.has(requestUrl)) return;

    // Oldest first — Map preserves insertion order.
    while (this.#prefetchCache.size >= VariantPicker.#PREFETCH_LIMIT) {
      const oldest = this.#prefetchCache.keys().next().value;
      if (oldest === undefined) break;
      this.#prefetchCache.delete(oldest);
    }

    this.#prefetchCache.set(
      requestUrl,
      fetch(requestUrl)
        .then((response) => response.text())
        .catch(() => {
          // Let the real request report the failure instead.
          this.#prefetchCache.delete(requestUrl);
          return '';
        })
    );
  };

  /**
   * The option values a click on `input` will actually request. This has to mirror
   * variantChanged rather than read the current selection: choosing an option clears everything
   * downstream of it (#clearDownstreamOptions) so the server re-resolves those, which means the
   * request carries *fewer* values than are checked right now. Predicting that wrong would just
   * warm a URL nobody asks for.
   *
   * The stand row is the exception — it repoints the Planter option without clearing, matching
   * reselectOptionValue, so the shopper keeps their colour.
   *
   * @param {HTMLInputElement} input - The input about to be selected.
   * @returns {string[] | null} Option value ids, or null when the outcome isn't predictable.
   */
  #predictedOptionValues(input) {
    const { optionValueId } = input.dataset;
    if (!optionValueId) return null;

    // Dropdown options keep a value rather than being cleared, and aren't worth modelling here.
    if (this.querySelector('select')) return null;

    const standRow = input.closest('plant-stand-picker');
    const fieldsets = Array.from(this.querySelectorAll('fieldset[data-fieldset-index]'));

    const anchor = standRow
      ? fieldsets.find((fieldset) => fieldset.getAttribute('data-option-name') === standRow.getAttribute('data-option-name'))
      : input.closest('fieldset[data-fieldset-index]');

    const anchorIndex = Number.parseInt(anchor?.getAttribute('data-fieldset-index') ?? '', 10);
    if (Number.isNaN(anchorIndex)) return null;

    /** @type {string[]} */
    const values = [];

    for (const fieldset of fieldsets) {
      const index = Number.parseInt(fieldset.getAttribute('data-fieldset-index') ?? '', 10);
      if (Number.isNaN(index)) continue;

      if (index === anchorIndex) {
        values.push(optionValueId);
        continue;
      }

      if (!standRow && index > anchorIndex) continue;

      const checked = fieldset.querySelector('input:checked');
      if (!(checked instanceof HTMLInputElement) || !checked.dataset.optionValueId) return null;

      values.push(checked.dataset.optionValueId);
    }

    return values;
  }

  /**
   * Re-resolves the variant after an outside control has repointed an option radio at
   * a different value of the option it already had checked — the "Include a plant
   * stand?" row, which selects a Planter value without being part of the Planter
   * fieldset (see plant-stand-picker.js).
   *
   * Unlike a change on the radio itself, downstream options are deliberately left
   * checked: adding or removing a stand shouldn't cost the shopper the color they
   * picked. If that color has no variant with the new stand, the server renders its
   * fieldset with nothing selected and the cascade in fetchUpdatedSection falls back
   * to the first available one.
   *
   * @param {HTMLInputElement} anchor - The repointed radio, used to build the request.
   * @param {string} optionValueId - The option value now selected.
   */
  reselectOptionValue(anchor, optionValueId) {
    this.#autoSelectDepth = 0;
    this.updateSelectedOption(anchor);
    this.dispatchEvent(new VariantSelectedEvent({ id: optionValueId }));
    this.fetchUpdatedSection(this.buildRequestUrl(anchor));
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @param {string[] | null} [optionValuesOverride] - Option values to request instead of the ones
   *   currently checked. Used to prefetch the result of a click that hasn't happened yet.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = [], optionValuesOverride = null) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    const optionValues = optionValuesOverride ?? this.selectedOptionsValues;

    if (optionValues.length && !source) {
      params.push(`option_values=${optionValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    // Product cards and quick-add already scope their request to a section; the product page was
    // the one caller left asking for the whole document. Only the product section is ever read
    // from the response — the picker, the gallery, the price, the buy buttons all live inside it
    // — so the rest was ~94% of a 1.4MB payload parsed and thrown away on every click.
    //
    // Combined listings are the exception: hopping to a sibling product morphs <main>, and only a
    // full page contains that (see morphElementSelector in variantChanged).
    const connectedProductUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = Boolean(connectedProductUrl) && connectedProductUrl !== this.dataset.productUrl?.split('?')[0];

    if (this.#isMainProductPicker && this.dataset.sectionId && !loadsNewProduct) {
      const path = productUrl?.includes('?') ? productUrl.split('?')[0] : productUrl;

      return `${path}?section_id=${this.dataset.sectionId}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    // A pointerdown on this option may already have this exact request in flight. Adopt it —
    // it's typically a head start of the whole pointerdown-to-click gap. Not abortable, but it
    // is already paid for, so letting it settle costs nothing.
    const prefetched = this.#prefetchCache.get(requestUrl);
    this.#prefetchCache.delete(requestUrl);

    const request = () => fetch(requestUrl, { signal: this.#abortController?.signal }).then((result) => result.text());

    // A prefetch that failed resolves empty rather than rejecting, so the click can quietly fall
    // back to a real request instead of inheriting a network blip that happened before it.
    const response = prefetched ? prefetched.then((responseText) => responseText || request()) : request();

    response
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // On the product page, changing an option (e.g. Size) can leave a
          // downstream option (Planter, then Color) with no selected value,
          // because this theme only renders *available* option values and the
          // previously selected value may no longer be available. When that
          // happens, select the first available value for that option and
          // re-fetch so the selection cascades top-down to a valid variant.
          if (
            this.#isMainProductPicker &&
            !newProduct &&
            this.#autoSelectDepth < this.#optionCount &&
            this.#selectFirstAvailableUnselectedOption()
          ) {
            this.#autoSelectDepth += 1;
            const { selectedOption } = this;
            if (selectedOption instanceof HTMLElement) {
              this.fetchUpdatedSection(this.buildRequestUrl(selectedOption));
              // Skip dispatching an update for this intermediate (null-variant)
              // state; the re-fetch resolves to the final selected variant.
              return;
            }
          }

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            const variant = JSON.parse(textContent);

            // Keep the ?variant param in sync with the finally-resolved variant.
            // variantChanged sets it optimistically from the clicked option, but
            // auto-cascading downstream options can change the final variant.
            this.#syncVariantIdInUrl(variant);

            this.dispatchEvent(
              new VariantUpdateEvent(variant, this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Whether this is the main variant picker on the product page (not a product
   * card or quick-add dialog). Auto-cascading selection only applies here.
   * @returns {boolean}
   */
  get #isMainProductPicker() {
    return (
      this.dataset.templateProductMatch === 'true' &&
      !this.closest('product-card') &&
      !this.closest('quick-add-dialog')
    );
  }

  /**
   * The number of option fieldsets (swatch/button style) rendered by the picker.
   * Used to bound the auto-selection re-fetch loop.
   * @returns {number}
   */
  get #optionCount() {
    return this.querySelectorAll('fieldset[data-fieldset-index]').length;
  }

  /**
   * Clears the selection of every option positioned after the one that was just
   * changed, so the re-render resolves each to its first available value. Only
   * fieldset (swatch/button) options are handled; dropdown options always keep a
   * selected value and are left untouched.
   * @param {Element} target - The input the user interacted with.
   */
  #clearDownstreamOptions(target) {
    if (!(target instanceof HTMLInputElement)) return;

    const changedIndex = Number.parseInt(target.dataset.fieldsetIndex ?? '', 10);
    if (Number.isNaN(changedIndex)) return;

    const fieldsets = Array.from(this.querySelectorAll('fieldset[data-fieldset-index]'));

    for (const fieldset of fieldsets) {
      const index = Number.parseInt(fieldset.getAttribute('data-fieldset-index') ?? '', 10);
      if (Number.isNaN(index) || index <= changedIndex) continue;

      for (const radio of fieldset.querySelectorAll('input[type="radio"]')) {
        if (radio instanceof HTMLInputElement) {
          radio.checked = false;
          radio.dataset.currentChecked = 'false';
        }
      }
    }
  }

  /**
   * Syncs the `?variant` URL parameter with the resolved variant. Only runs for
   * the main product picker; other contexts manage their own URLs.
   * @param {{ id?: number | string } | null | undefined} variant - The resolved variant object.
   */
  #syncVariantIdInUrl(variant) {
    if (!this.#isMainProductPicker) return;

    const url = new URL(window.location.href);
    const variantId = variant?.id;

    if (variantId) {
      if (url.searchParams.get('variant') === String(variantId)) return;
      url.searchParams.set('variant', String(variantId));
    } else {
      if (!url.searchParams.has('variant')) return;
      url.searchParams.delete('variant');
    }

    requestYieldCallback(() => {
      history.replaceState({}, '', url.toString());
    });
  }

  /**
   * Finds the first option fieldset that has no value selected and selects its
   * first available value. Because the theme only renders available values, the
   * first radio in a fieldset is that option's first available value, and a
   * fieldset with nothing checked means its previously selected value is no
   * longer available for the current upstream selection.
   *
   * Queries the DOM directly rather than `this.refs.fieldsets` because refs are
   * updated asynchronously (via MutationObserver) after a morph.
   * @returns {boolean} True if a value was selected and a re-fetch is needed.
   */
  #selectFirstAvailableUnselectedOption() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset[data-fieldset-index]'));

    for (const fieldset of fieldsets) {
      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]')).filter(
        (radio) => radio instanceof HTMLInputElement
      );
      if (radios.length === 0) continue;

      const hasChecked = radios.some((radio) => radio.checked);
      if (hasChecked) continue;

      const [firstAvailable] = radios;
      if (firstAvailable) {
        firstAvailable.checked = true;
        firstAvailable.dataset.currentChecked = 'true';
        return true;
      }
    }

    return false;
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
